import type { ReactElement } from "react";
import { ErrorState } from "./state-components/error-state";
import { usePageLayoutState } from "./hooks/use-page-layout-state";
import { PageHeader } from "./shared-components/page-header";
import { PageWrapper } from "./shared-components/page-wrapper";
import type { DataTablePageLayoutProps } from "./types";

/**
 * DataTablePageLayout - Page layout for DataTable/ServerDataTable pages
 *
 * Handles loading and error states. Always renders children on success,
 * even with empty data — the table component handles its own empty state
 * (e.g., "Pas de résultats.") while keeping the toolbar/search visible.
 *
 * Supports optional back navigation via `backLink`/`backLabel`.
 */
export function DataTablePageLayout<T>({
  fetching,
  data,
  title,
  subtitle,
  action,
  children,
  loadingComponent,
  errorStateProps,
  className,
  keepDataWhileRefetching,
  backLink,
  backLabel,
}: DataTablePageLayoutProps<T>): ReactElement {
  const { isLoading, isError } = usePageLayoutState({
    fetching,
    idleAsLoading: true,
    keepDataWhileRefetching,
  });

  const header = <PageHeader title={title} subtitle={subtitle} action={action} backLink={backLink} backLabel={backLabel} />;

  // Render loading state
  if (isLoading) {
    return (
      <PageWrapper className={className}>
        {header}
        <div className="rounded-lg border bg-card p-4">{loadingComponent}</div>
      </PageWrapper>
    );
  }

  // Render error state
  if (isError) {
    return (
      <PageWrapper className={className}>
        {header}
        <div className="rounded-lg border bg-card p-4">
          <ErrorState {...errorStateProps} />
        </div>
      </PageWrapper>
    );
  }

  // Always render children — the table handles its own empty state
  return (
    <PageWrapper className={className}>
      {header}
      <div className="rounded-lg border bg-card p-4">{children(data ?? ([] as T[]))}</div>
    </PageWrapper>
  );
}
